import * as Dialog from "@radix-ui/react-dialog";
import { Copy, Plug, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Button, StatusBadge } from "@/components/ui-ext/primitives";
import { cn } from "@/lib/utils";

import { cosmosApiUrl } from "@/lib/cosmos-api-base-url";

const repoPathPlaceholder = "<absolute-path-to-AIHR.Cosmos>";

const mcpConfig = {
  mcpServers: {
    "aihr-cosmos": {
      command: "npm",
      args: ["run", "mcp:start"],
      cwd: repoPathPlaceholder,
      env: {
        COSMOS_QUERY_EXECUTION_ENGINE: "seed",
        COSMOS_AUTH_MODE: "header"
      }
    }
  }
};

type Tab = "mcp" | "openapi" | "auth";

const tabs: Array<{ id: Tab; label: string }> = [
  { id: "mcp", label: "Cursor / Claude" },
  { id: "openapi", label: "OpenAPI" },
  { id: "auth", label: "Headers / Auth" }
];

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Button type="button" variant="secondary" size="sm" onClick={() => void copy()}>
      <Copy className="h-3.5 w-3.5" />
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

function CodeBlock({ value }: { value: string }) {
  return (
    <div className="relative">
      <pre className="max-h-[360px] overflow-auto rounded-[12px] border border-soft bg-background p-4 text-xs text-anchor">
        {value}
      </pre>
      <div className="absolute right-3 top-3">
        <CopyButton value={value} />
      </div>
    </div>
  );
}

export function AiIntegrationDialog() {
  const [tab, setTab] = useState<Tab>("mcp");
  const openApiUrl = cosmosApiUrl("/api/spec/openapi");
  const mcpUrl = cosmosApiUrl("/api/spec/mcp");
  const mcpJson = useMemo(() => JSON.stringify(mcpConfig, null, 2), []);
  const curlExample = useMemo(
    () =>
      [
        `curl ${openApiUrl} > cosmos-openapi.json`,
        "npx @postman/cli collection import cosmos-openapi.json"
      ].join("\n"),
    [openApiUrl]
  );

  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>
        <Button variant="secondary">
          <Plug className="h-4 w-4" />
          Integrate with your AI tool
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/35" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(920px,calc(100vw-2rem))] max-h-[calc(100vh-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[18px] border border-soft bg-surface shadow-pop">
          <div className="flex items-start justify-between gap-4 border-b border-soft p-5">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <StatusBadge tone="ai" dot>
                  MCP + OpenAPI
                </StatusBadge>
              </div>
              <Dialog.Title className="text-xl font-bold text-anchor">
                Integrate Cosmos with AI tools
              </Dialog.Title>
              <Dialog.Description className="mt-1 max-w-2xl text-sm text-muted-foreground">
                Use the stdio MCP server for agent integrations, or import the live
                OpenAPI document into any client that understands REST specs.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                className="rounded-[10px] p-2 text-muted-foreground hover:bg-muted hover:text-anchor"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex flex-wrap gap-2 border-b border-soft px-5 py-3">
            {tabs.map((item) => (
              <button
                key={item.id}
                className={cn(
                  "rounded-full px-3 py-1.5 text-xs font-medium",
                  tab === item.id
                    ? "bg-[var(--primary)] text-[var(--primary-foreground)]"
                    : "bg-muted text-muted-foreground hover:text-anchor"
                )}
                onClick={() => setTab(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="max-h-[calc(100vh-16rem)] overflow-auto p-5">
            {tab === "mcp" && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Add this block to Cursor or Claude Desktop, replacing the
                  repository path with your local `AIHR.Cosmos` checkout.
                </p>
                <CodeBlock value={mcpJson} />
              </div>
            )}

            {tab === "openapi" && (
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <a
                    href={openApiUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-[12px] border border-soft bg-background p-4 text-sm text-anchor hover:border-[var(--primary)]"
                  >
                    Download OpenAPI JSON
                    <div className="mt-1 text-xs text-muted-foreground">{openApiUrl}</div>
                  </a>
                  <a
                    href={mcpUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-[12px] border border-soft bg-background p-4 text-sm text-anchor hover:border-[var(--primary)]"
                  >
                    Download MCP catalog
                    <div className="mt-1 text-xs text-muted-foreground">{mcpUrl}</div>
                  </a>
                </div>
                <CodeBlock value={curlExample} />
              </div>
            )}

            {tab === "auth" && (
              <div className="space-y-4 text-sm text-muted-foreground">
                <p>
                  In `COSMOS_AUTH_MODE=auth0`, calls use an Auth0 bearer token. In
                  local/header mode, Cosmos scopes requests with these headers:
                </p>
                <CodeBlock
                  value={[
                    "x-cosmos-tenant-id: default-tenant",
                    "x-cosmos-user-id: local-cosmos-user",
                    "x-cosmos-roles: admin,operator"
                  ].join("\n")}
                />
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
